import { useContext, useEffect } from 'react'
import ErrorScreen from '../../Misc/ErrorScreen'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Chip,
  Grid,
  Link as LinkMui,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { invoiceType } from '../../types'
import { dataContext } from '../..'
import { Link as LinkRouter } from 'react-router-dom'
import dayjs from 'dayjs'
import { ExpandMore } from '@mui/icons-material'
import { DatePicker, getRepCodeName } from '../../helper'

function InvoiceList(props: {
  invoiceList: (invoiceType & {
    customer_name: string
    job_name: string
  })[]
  invoiceCount: number
  filters: { [index: string]: string | number }
  searchError: boolean
  initialLoad: boolean
  listLoading: boolean
  handleSearch: (filters: { [index: string]: string | number }) => void
  setFilters: (filters: { [index: string]: string | number }) => void
}) {
  const { user, salesReps } = useContext(dataContext)

  useEffect(() => {
    if (user.active_flag) {
      props.handleSearch(props.filters)
    }
  }, [user])

  if (props.searchError) {
    return (
      <div className="customer-list-error-container">
        <ErrorScreen
          errorText="Something went wrong while searching. Please try again or contact support if the error persists."
          title="Search Failed"
        />
      </div>
    )
  }

  return (
    <TableContainer className="customer-list-table">
      <div className="customer-pagination-header">
        <div className="customer-list-title">
          {props.initialLoad && props.listLoading ? (
            <Skeleton width={100} height={32} />
          ) : (
            <Typography variant="h6">Invoices</Typography>
          )}
        </div>
        {props.initialLoad && props.listLoading ? (
          <Skeleton width={300} height={32} />
        ) : (
          <TablePagination
            component="div"
            count={props.invoiceCount}
            page={props.filters.page as number}
            rowsPerPage={props.filters.limit as number}
            onPageChange={(_e, newPage) => {
              const newFilters = { ...props.filters, page: newPage }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
            onRowsPerPageChange={e => {
              const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
          />
        )}
      </div>
      <Table stickyHeader color="primary">
        <TableHead>
          {props.initialLoad && props.listLoading ? (
            <TableRow>
              {Array.from({ length: 10 }).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton className="skeleton-header" />
                </TableCell>
              ))}
            </TableRow>
          ) : (
            <TableRow>
              <TableCell width="5%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('ref_number')}
                  direction={props.filters.sort === 'ref_number' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'ref_number' ? 'ref_number desc' : 'ref_number',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('customer_name')}
                  direction={props.filters.sort === 'customer_name' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'customer_name' ? 'customer_name desc' : 'customer_name',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Customer Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('job_name')}
                  direction={props.filters.sort === 'job_name' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'job_name' ? 'job_name desc' : 'job_name',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Job Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('rep_code')}
                  direction={props.filters.sort === 'rep_code' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'rep_code' ? 'rep_code desc' : 'rep_code',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Sales Rep
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('txn_date')}
                  direction={props.filters.sort === 'txn_date' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'txn_date' ? 'txn_date desc' : 'txn_date',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Invoice Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('due_date')}
                  direction={props.filters.sort === 'due_date' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'due_date' ? 'due_date desc' : 'due_date',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('total_amount')}
                  direction={props.filters.sort === 'total_amount' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'total_amount' ? 'total_amount desc' : 'total_amount',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Total Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('applied_amount')}
                  direction={props.filters.sort === 'applied_amount' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'applied_amount' ? 'applied_amount desc' : 'applied_amount',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Applied Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('balance_amount')}
                  direction={props.filters.sort === 'balance_amount' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'balance_amount' ? 'balance_amount desc' : 'balance_amount',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Balance Amount
                </TableSortLabel>
              </TableCell>
              <TableCell width="5%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('is_paid')}
                  direction={props.filters.sort === 'is_paid' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'is_paid' ? 'is_paid desc' : 'is_paid',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Paid
                </TableSortLabel>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {props.listLoading
            ? Array.from({ length: props.filters.limit as number }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: 10 }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : props.invoiceList.map(invoice => (
                <TableRow key={invoice.invoice_txn_id}>
                  <TableCell>
                    <LinkMui to={`/customer/invoice/${invoice.invoice_txn_id}`} component={LinkRouter}>
                      {invoice.ref_number}
                    </LinkMui>
                  </TableCell>
                  <TableCell>{invoice.customer_name ? invoice.customer_name : '-'}</TableCell>
                  <TableCell>{invoice.job_name ? invoice.job_name : '-'}</TableCell>
                  <TableCell>{`${invoice.rep_code} ${getRepCodeName(
                    salesReps.find(rep => rep.initial === invoice.rep_code),
                  )}`}</TableCell>
                  <TableCell align="right">
                    {invoice.txn_date ? dayjs.utc(invoice.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {invoice.due_date ? dayjs.utc(invoice.due_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {((invoice.balance_amount ?? 0) - (invoice.applied_amount ?? 0)).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {invoice.applied_amount
                      ? invoice.applied_amount.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {invoice.balance_amount
                      ? invoice.balance_amount.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.is_paid ? 'Paid' : 'Unpaid'}
                      color={invoice.is_paid ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <TablePagination
        className="customer-pagination-footer"
        component="div"
        count={props.invoiceCount}
        page={props.filters.page as number}
        rowsPerPage={props.filters.limit as number}
        onPageChange={(_e, newPage) => {
          const newFilters = { ...props.filters, page: newPage }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
        onRowsPerPageChange={e => {
          const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
      />
    </TableContainer>
  )
}

export function InvoiceSearch(props: {
  filters: { [index: string]: string | number }
  initialLoad: boolean
  listLoading: boolean
  handleSearch: (filters: { [index: string]: string | number }) => void
  setFilters: (filters: { [index: string]: string | number }) => void
}) {
  const { customers } = useContext(dataContext)

  const handleClear = () => {
    const newFilters = {
      page: 0,
      limit: 25,
      sort: 'due_date desc',
      invoiceNumber: '',
      invoiceStatus: '',
      customerName: '',
      fromDate: '',
      toDate: '',
      dueDateStart: '',
      dueDateEnd: '',
      soNumber: '',
      serialNumber: '',
    }

    props.setFilters(newFilters)
    props.handleSearch(newFilters)
  }

  if (props.initialLoad && props.listLoading) {
    return (
      <form>
        <div className="customer-input-row">
          <Skeleton width={120} height={24} />
          <Skeleton variant="rounded" width={200} height={40} className="customer-name-input" />
          <Button
            size="large"
            variant="contained"
            type="submit"
            onClick={e => {
              e.preventDefault()
              props.handleSearch(props.filters)
            }}
            loading={props.listLoading}
          >
            Search
          </Button>
          <Button size="large" variant="outlined" onClick={handleClear} loading={props.listLoading}>
            Clear
          </Button>
        </div>
        <Accordion className="customer-advanced-search">
          <AccordionSummary expandIcon={<ExpandMore className="customer-advanced-search-expand-icon" />}>
            <Skeleton width={150} height={24} />
          </AccordionSummary>
        </Accordion>
      </form>
    )
  }

  return (
    <form>
      <div className="customer-input-row">
        <Typography variant="body2">Invoice Number:</Typography>
        <TextField
          className="customer-name-input"
          label="Invoice No."
          size="small"
          value={props.filters.invoiceNumber}
          onChange={e => {
            props.setFilters({
              ...props.filters,
              invoiceNumber: e.target.value,
            })
          }}
        />
        <Button
          size="large"
          variant="contained"
          type="submit"
          onClick={e => {
            e.preventDefault()
            props.handleSearch(props.filters)
          }}
          loading={props.listLoading && props.initialLoad}
        >
          Search
        </Button>
        <Button size="large" variant="outlined" onClick={handleClear} loading={props.listLoading && props.initialLoad}>
          Clear
        </Button>
      </div>
      <Accordion className="customer-advanced-search">
        <AccordionSummary expandIcon={<ExpandMore className="customer-advanced-search-expand-icon" />}>
          <Typography>Advanced Search</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={'17px'}>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={100} height={24} />
              ) : (
                <Typography variant="body2">Invoice Status:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  label="Invoice Status"
                  value={props.filters.invoiceStatus}
                  select
                  fullWidth
                  onChange={e => {
                    props.setFilters({
                      ...props.filters,
                      invoiceStatus: e.target.value,
                    })
                  }}
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                </TextField>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={100} height={24} />
              ) : (
                <Typography variant="body2">Invoice Date:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton variant="rounded" width="50%" height={40} />
                  <Skeleton variant="rounded" width="50%" height={40} />
                </div>
              ) : (
                <div className="customer-advanced-search-date-group">
                  <DatePicker
                    label="From Date"
                    size="small"
                    value={
                      props.filters.salesOrderDateStart
                        ? dayjs.utc(props.filters.salesOrderDateStart as string)
                        : undefined
                    }
                    onChange={e => {
                      props.setFilters({
                        ...props.filters,
                        salesOrderDateStart: e?.format('YYYY-MM-DD') ?? '',
                      })
                    }}
                  />
                  <DatePicker
                    label="To Date"
                    size="small"
                    value={
                      props.filters.salesOrderDateEnd ? dayjs.utc(props.filters.salesOrderDateEnd as string) : undefined
                    }
                    onChange={e => {
                      props.setFilters({
                        ...props.filters,
                        salesOrderDateEnd: e?.format('YYYY-MM-DD') ?? '',
                      })
                    }}
                  />
                </div>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={120} height={24} />
              ) : (
                <Typography variant="body2">Customer Name:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <Autocomplete
                  autoHighlight
                  options={Array.from(customers.values())
                    .filter(customer => !customer.parent_list_id)
                    .map(({ customer_list_id, name }) => ({
                      id: customer_list_id,
                      label: name,
                    }))}
                  value={
                    Array.from(customers.values())
                      .map(customer => ({ id: customer.customer_list_id, label: customer.name }))
                      .find(customer => customer.id === props.filters.customerName) ?? null
                  }
                  renderInput={params => <TextField {...params} size="small" label="Customer Name" />}
                  fullWidth
                  onChange={(_e, value) => {
                    props.setFilters({
                      ...props.filters,
                      customerName: value?.id ?? '',
                    })
                  }}
                />
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={80} height={24} />
              ) : (
                <Typography variant="body2">Due Date:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton variant="rounded" width="50%" height={40} />
                  <Skeleton variant="rounded" width="50%" height={40} />
                </div>
              ) : (
                <div className="customer-advanced-search-date-group">
                  <DatePicker
                    label="From Date"
                    size="small"
                    value={props.filters.dueDateStart ? dayjs.utc(props.filters.dueDateStart as string) : undefined}
                    onChange={e => {
                      props.setFilters({
                        ...props.filters,
                        dueDateStart: e?.format('YYYY-MM-DD') ?? '',
                      })
                    }}
                  />
                  <DatePicker
                    label="To Date"
                    size="small"
                    value={props.filters.dueDateEnd ? dayjs.utc(props.filters.dueDateEnd as string) : undefined}
                    onChange={e => {
                      props.setFilters({
                        ...props.filters,
                        dueDateEnd: e?.format('YYYY-MM-DD') ?? '',
                      })
                    }}
                  />
                </div>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={120} height={24} />
              ) : (
                <Typography variant="body2">Serial Number:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  label="Serial Number"
                  value={props.filters.serialNumber}
                  fullWidth
                  onChange={e => {
                    props.setFilters({
                      ...props.filters,
                      serialNumber: e.target.value,
                    })
                  }}
                />
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton width={100} height={24} />
              ) : (
                <Typography variant="body2">SO Number:</Typography>
              )}
            </Grid>
            <Grid size={3} className="customer-advanced-search-grid">
              {props.listLoading && props.initialLoad ? (
                <Skeleton variant="rounded" width="100%" height={40} />
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  label="SO Number"
                  value={props.filters.soNumber}
                  fullWidth
                  onChange={e => {
                    props.setFilters({
                      ...props.filters,
                      soNumber: e.target.value,
                    })
                  }}
                />
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </form>
  )
}

export default InvoiceList
